import { useState, useEffect } from "react";

export const POS_SETTINGS_STORAGE_KEY = "bazar_pos_settings";
export const POS_SETTINGS_CHANGE_EVENT = "posSettingsChange";

export interface PosSettings {
  itemsPerPage: number;
  dateFormat: "relative" | "absolute";
  lowStockDefault: number;
  currencySymbol: string;
  sidebarCollapsedDefault: boolean;
  soundAlertsEnabled: boolean;
  storeName?: string;
}

export const DEFAULT_POS_SETTINGS: PosSettings = {
  itemsPerPage: 20,
  dateFormat: "absolute",
  lowStockDefault: 10,
  currencySymbol: "PKR",
  sidebarCollapsedDefault: false,
  soundAlertsEnabled: true,
  storeName: "",
};

export const getPosSettings = (): PosSettings => {
  try {
    const raw = typeof window !== "undefined" && window.localStorage
      ? localStorage.getItem(POS_SETTINGS_STORAGE_KEY)
      : null;
    if (!raw) return { ...DEFAULT_POS_SETTINGS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_POS_SETTINGS, ...parsed };
  } catch {
    return { ...DEFAULT_POS_SETTINGS };
  }
};

export const savePosSettings = (settings: PosSettings): void => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(POS_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      window.dispatchEvent(new CustomEvent(POS_SETTINGS_CHANGE_EVENT, { detail: settings }));
    }
  } catch (err) {
    console.error("Failed to save POS settings:", err);
  }
};

export const resetPosSettings = (): void => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem(POS_SETTINGS_STORAGE_KEY);
      window.dispatchEvent(new CustomEvent(POS_SETTINGS_CHANGE_EVENT, { detail: { ...DEFAULT_POS_SETTINGS } }));
    }
  } catch (err) {
    console.error("Failed to reset POS settings:", err);
  }
};

export const usePosSettings = (): PosSettings => {
  const [settings, setSettings] = useState<PosSettings>(getPosSettings);

  useEffect(() => {
    const handleSettingsChange = (event: Event) => {
      const customEvent = event as CustomEvent<PosSettings>;
      if (customEvent.detail) {
        setSettings(customEvent.detail);
      } else {
        setSettings(getPosSettings());
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === POS_SETTINGS_STORAGE_KEY || !e.key) {
        setSettings(getPosSettings());
      }
    };

    window.addEventListener(POS_SETTINGS_CHANGE_EVENT, handleSettingsChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(POS_SETTINGS_CHANGE_EVENT, handleSettingsChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return settings;
};
